<script setup lang="ts">
    import { reactive, ref, computed, watch, onMounted, inject } from 'vue';
    import { useRouter, useRoute } from 'vue-router';
    import * as smsStatsApi from './TextMessageStatistics.access';
    import type { SmsSent } from '../interfaces';
    import SmsStatisticsHighlights from './components/SmsStatisticsHighlights.vue';
    import SmsStatisticsChart from './components/SmsStatisticsChart.vue';
    import SmsStatisticsExpenses from './components/SmsStatisticsExpenses.vue';
    import SmsStatisticsPreview from './components/SmsStatisticsPreview.vue';
    import SmsStatisticsListHeader from './components/SmsStatisticsListHeader.vue';
    import SmsStatisticsNavbar from './components/SmsStatisticsNavbar.vue';
    import SmsContactList from './components/SmsContactList.vue';
    import Tabs from '@shared/components/Tabs.vue';
    import Tab from '@shared/components/Tab.vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { AlertTypes } from '@shared/components/BsAlerts';
    import { formatDate } from '@shared/utils/formatDateTime';
    import Loading from '@shared/components/Loading.vue';
    import StatisticsContactFilterAlert from '@views/Lists/components/ContactFilterAlert.vue';
    import type { SerializedContactFilterState } from '@views/Lists/components/ContactFilter.vue';
    import { useContactAdditionalFields } from '@views/Lists/useContactAdditionalFields';
    import { ContextAccess } from '@shared/Root/Context.access';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';

    const props = defineProps<{
        smsMessageId: string;
        contactType?: string;
    }>();

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const contextAccess: ContextAccess = injectorAccess.injector.get(ContextAccess);
    const { availableFields } = useContactAdditionalFields(contextAccess, { hideAddedDateTime: true });

    const aggregateContactFilter = ref('');
    const contactFilterState = ref<Partial<SerializedContactFilterState> | null>(null);
    const navbarRef = ref<InstanceType<typeof SmsStatisticsNavbar>>();
    const contactFilterRef = computed(() => navbarRef.value?.contactFilterRef);

    const router = useRouter();
    const route = useRoute();
    const showContactList = computed(() => !!props.contactType);
    const showingContact = computed(() => route.name === 'TextMessageStatisticsContact');

    const state = reactive({
        loading: false,
        overview: null as SmsSent | null,
        dateRange: { start: null, end: null } as { start: string | null; end: string | null },
    });

    function buildQs() {
        return smsStatsApi.buildQueryString(state.dateRange, aggregateContactFilter.value || null);
    }

    let overviewAbortController: AbortController | null = null;

    async function loadOverview() {
        overviewAbortController?.abort();
        overviewAbortController = new AbortController();
        const { signal } = overviewAbortController;
        state.loading = true;
        try {
            state.overview = await smsStatsApi.getSmsMessage(props.smsMessageId, buildQs(), signal);
        } catch (e) {
            if (e instanceof DOMException && e.name === 'AbortError') return;
            throw e;
        } finally {
            if (!signal.aborted) state.loading = false;
        }
    }

    function onDateRangeChange(start: string, end: string) {
        state.dateRange.start = start || null;
        state.dateRange.end = end || null;
    }

    function setupDateRangeWatch() {
        watch(
            () => [state.dateRange.start, state.dateRange.end],
            ([start, end]) => {
                const query = { ...route.query };
                if (start) query.start = start as string;
                else delete query.start;
                if (end) query.end = end as string;
                else delete query.end;
                router.replace({ query });
                loadOverview();
            }
        );
    }

    function setupContactFilterWatch() {
        watch(
            [aggregateContactFilter, contactFilterState],
            ([f, s]) => {
                const query = { ...route.query };
                if (f) query.contactFilter = f;
                else delete query.contactFilter;
                if (s && Object.keys(s).length > 0) query.contactFilterState = JSON.stringify(s);
                else delete query.contactFilterState;
                router.replace({ query });
                loadOverview();
            },
            { deep: true }
        );
    }

    function restoreContactFilterFromUrl() {
        if (typeof route.query.contactFilter === 'string') aggregateContactFilter.value = route.query.contactFilter;

        const raw = route.query.contactFilterState;
        if (!raw || typeof raw !== 'string') return;
        try {
            const parsed = JSON.parse(raw) as Partial<SerializedContactFilterState>;
            contactFilterRef.value?.restoreFilterState(parsed);
        } catch {
            /* ignore malformed query string */
        }
    }

    const hasDateFilter = computed(() => !!state.dateRange.start || !!state.dateRange.end);

    const newVersionAlertContent = () => {
        const url = `/TextMessages/${props.smsMessageId}/Statistics/Old`;
        return $Resources.GeneralNewVersionAlertContent.replace('{{', `<a href="${url}">`).replace('}}', '</a>');
    };

    onMounted(async () => {
        if (route.query.start) state.dateRange.start = route.query.start as string;
        if (route.query.end) state.dateRange.end = route.query.end as string;
        restoreContactFilterFromUrl();
        await loadOverview();
        setupDateRangeWatch();
        setupContactFilterWatch();
    });
</script>

<template>
    <BsAlert
        :show="!showContactList && !showingContact"
        class="m-0-b"
        :type="AlertTypes.Info"
        :title="$Resources.GeneralNewVersionAlertTitle"
        :content="newVersionAlertContent()" />

    <SmsStatisticsNavbar
        v-if="!showingContact"
        ref="navbarRef"
        :showContactList="showContactList"
        :contactType="contactType"
        :dateRange="state.dateRange"
        :availableFields="availableFields"
        @update:dateRange="onDateRangeChange"
        @update:contactFilter="(f) => (aggregateContactFilter = f)"
        @update:contactFilterState="(s) => (contactFilterState = s)" />

    <template v-if="showContactList">
        <SmsContactList
            :smsMessageId="smsMessageId"
            :contactType="contactType!"
            :dateRange="state.dateRange"
            :contactFilter="aggregateContactFilter" />
    </template>

    <template v-else>
        <SmsStatisticsListHeader :overview="state.overview" />

        <div class="alert-group">
            <BsAlert
                icon="filter"
                :show="hasDateFilter"
                :type="AlertTypes.Info"
                :title="$Resources.DateRangeFilterOnAlertHeading"
                :dismissible="false"
                :buttonText="$Resources.ClearBtn"
                @buttonClick="
                    () => {
                        state.dateRange.start = null;
                        state.dateRange.end = null;
                    }
                ">
                <template #content>
                    {{ formatDate(state.dateRange.start) || '-' }}
                    <span v-show="state.dateRange.end && state.dateRange.start !== state.dateRange.end">
                        {{ $Resources.DateRangeSeparator }} {{ formatDate(state.dateRange.end) || '-' }}
                    </span>
                </template>
            </BsAlert>

            <StatisticsContactFilterAlert :filterRef="contactFilterRef" :totalCount="state.overview?.RecipientCount ?? 0" />
        </div>

        <div class="container-fluid">
            <SmsStatisticsHighlights :overview="state.overview" />

            <div class="row m-lg-t">
                <div class="col-md-7">
                    <Tabs :queryParam="false">
                        <Tab :title="$Resources.IssueStatisticsSummaryTab">
                            <SmsStatisticsChart :overview="state.overview" />
                        </Tab>
                        <Tab :title="$Resources.SmsFinalCostTab">
                            <SmsStatisticsExpenses :overview="state.overview" />
                        </Tab>
                    </Tabs>
                </div>
                <div class="col-md-5">
                    <SmsStatisticsPreview :overview="state.overview" />
                </div>
            </div>
        </div>
    </template>

    <Loading :show="state.loading" loadingStyle="progress-bar" :fixed="true" />
</template>
